import { useEffect, useState } from 'react';
import type { MetricRange, MetricWindow } from '../api/types';

const MINUTE = 60_000;
const HOUR = 3_600_000;
const DAY = 86_400_000;
/** これより古い時間帯は Panel に残っていない（1 時間平均を 90 日残す） */
const MAX_AGE_MS = 90 * DAY;
const ranges: Array<{ range: MetricRange; label: string; span: number }> = [
  { range: '15m', label: '15 分', span: 15 * MINUTE },
  { range: '30m', label: '30 分', span: 30 * MINUTE },
  { range: '1h', label: '1 時間', span: HOUR },
  { range: '3h', label: '3 時間', span: 3 * HOUR },
  { range: '6h', label: '6 時間', span: 6 * HOUR },
  { range: '12h', label: '12 時間', span: 12 * HOUR },
  { range: '24h', label: '24 時間', span: DAY },
  { range: '3d', label: '3 日', span: 3 * DAY },
  { range: '7d', label: '7 日', span: 7 * DAY },
  { range: '14d', label: '14 日', span: 14 * DAY },
  { range: '30d', label: '30 日', span: 30 * DAY },
  { range: '90d', label: '90 日', span: 90 * DAY },
];

/** 期間の開始・終了（ms）。決まった範囲は「今まで」 */
export function windowBounds(period: MetricWindow, now = Date.now()) {
  if (typeof period !== 'string') return { from: Date.parse(period.from), to: Date.parse(period.to) };
  const span = ranges.find((r) => r.range === period)?.span ?? HOUR;
  return { from: now - span, to: now };
}

/** 決まった範囲（今まで）のときだけ 30 秒ごとに取り直す。過去の期間は変わらないので取り直さない */
export function windowPollMs(period: MetricWindow) {
  return typeof period === 'string' ? 30000 : 0;
}

const pad = (n: number) => String(n).padStart(2, '0');
/** datetime-local の値（手元の時刻） */
function toLocalInput(ms: number) {
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function label(ms: number) {
  return new Date(ms).toLocaleString('ja-JP', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/**
 * Zabbix のような期間の選び方: 決まった範囲、開始〜終了の指定、前後への移動。
 * presets={false} のときは決まった範囲のボタンを出さず、開始〜終了の指定だけにする（初めは value の範囲）
 */
export function MetricWindowPicker({
  value,
  onChange,
  presets = true,
}: {
  value: MetricWindow;
  onChange: (next: MetricWindow) => void;
  presets?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const { from, to } = windowBounds(value);
  const [draft, setDraft] = useState({ from: '', to: '' });
  const [live] = useState(value);
  const span = to - from;
  const now = Date.now();
  const valueKey = JSON.stringify(value);

  // 指定だけの形では入力欄をいつも出すので、表示中の期間に合わせておく
  useEffect(() => {
    if (presets) return;
    const bounds = windowBounds(value);
    setDraft({ from: toLocalInput(bounds.from), to: toLocalInput(bounds.to) });
  }, [presets, valueKey]);

  const shift = (direction: -1 | 1) => {
    const nextTo = Math.min(to + direction * span, now);
    const nextFrom = Math.max(nextTo - span, now - MAX_AGE_MS);
    // 今に追いついたら、同じ長さの決まった範囲に戻して自動の更新を続ける
    const preset = ranges.find((r) => r.span === span);
    if (nextTo >= now && preset) onChange(preset.range);
    else onChange({ from: new Date(nextFrom).toISOString(), to: new Date(nextTo).toISOString() });
  };
  const openEditor = () => {
    setDraft({ from: toLocalInput(from), to: toLocalInput(to) });
    setEditing(true);
  };
  const draftFrom = Date.parse(draft.from);
  const draftTo = Date.parse(draft.to);
  const draftError =
    Number.isNaN(draftFrom) || Number.isNaN(draftTo)
      ? '開始と終了を入れてください。'
      : draftTo - draftFrom < 60_000
        ? '終了は開始より 1 分以上あとにしてください。'
        : draftTo < now - MAX_AGE_MS
          ? '90 日より前の値は残っていません。'
          : undefined;
  const apply = () => {
    if (draftError) return;
    onChange({ from: new Date(draftFrom).toISOString(), to: new Date(Math.min(draftTo, now)).toISOString() });
    setEditing(false);
  };

  const moveButtons = (
    <div className="row" role="group" aria-label="期間を動かす" style={{ gap: 6 }}>
      <button className="btn small" onClick={() => shift(-1)} disabled={from <= now - MAX_AGE_MS} aria-label="前の期間">
        ◀ 前へ
      </button>
      <button className="btn small" onClick={() => shift(1)} disabled={typeof value === 'string'} aria-label="次の期間">
        次へ ▶
      </button>
    </div>
  );
  const rangeInputs = (
    <>
      <label className="field">
        <span className="small">開始</span>
        <input
          className="input"
          type="datetime-local"
          value={draft.from}
          onChange={(event) => setDraft({ ...draft, from: event.target.value })}
        />
      </label>
      <label className="field">
        <span className="small">終了</span>
        <input
          className="input"
          type="datetime-local"
          value={draft.to}
          onChange={(event) => setDraft({ ...draft, to: event.target.value })}
        />
      </label>
      <button className="btn small primary" onClick={apply} disabled={!!draftError}>
        表示
      </button>
    </>
  );

  if (!presets) {
    const liveLabel = typeof value === 'string' ? ranges.find((r) => r.range === value)?.label : undefined;
    return (
      <div className="stack" style={{ gap: 8 }}>
        <div className="row" style={{ flexWrap: 'wrap', gap: 8, alignItems: 'end' }}>
          {rangeInputs}
          {typeof value !== 'string' && (
            <button className="btn small" onClick={() => onChange(live)}>
              最新に戻す
            </button>
          )}
          <span className="spacer" />
          {moveButtons}
        </div>
        <p className="small muted">
          {draftError && <>{draftError} </>}
          {liveLabel
            ? `直近 ${liveLabel}を表示しています（30 秒ごとに更新）。`
            : `${label(from)} 〜 ${label(to)} を表示しています。`}
          グラフをドラッグすると、その部分を拡大します。
        </p>
      </div>
    );
  }

  return (
    <div className="stack" style={{ gap: 8 }}>
      <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
        <div className="row" role="group" aria-label="期間" style={{ flexWrap: 'wrap', gap: 6 }}>
          {ranges.map((r) => (
            <button
              key={r.range}
              className={`btn small${value === r.range ? ' primary' : ''}`}
              onClick={() => {
                setEditing(false);
                onChange(r.range);
              }}
            >
              {r.label}
            </button>
          ))}
          <button
            className={`btn small${typeof value !== 'string' || editing ? ' primary' : ''}`}
            onClick={() => (editing ? setEditing(false) : openEditor())}
            aria-expanded={editing}
          >
            期間を指定
          </button>
        </div>
        <span className="spacer" />
        {moveButtons}
      </div>
      {typeof value !== 'string' && (
        <p className="small muted">
          {label(from)} 〜 {label(to)}（グラフをドラッグすると、その部分を拡大します）
        </p>
      )}
      {typeof value === 'string' && !editing && (
        <p className="small muted">グラフをドラッグすると、その部分を拡大します。</p>
      )}
      {editing && (
        <div className="row" style={{ flexWrap: 'wrap', gap: 8, alignItems: 'end' }}>
          {rangeInputs}
          {draftError && <span className="small muted">{draftError}</span>}
        </div>
      )}
    </div>
  );
}
